import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {createPaddleCustomer} from '../store/slices/subscriptionThunks';
import {userSelector} from '../store/selectors/auth.selectors';
import {initializePaddle, openPaddleCheckout, updatePaddleCustomer} from '../lib/paddle';
import {useToast} from './Toast';
import {debugLog} from '../lib/debug';

/**
 * Opens the Paddle overlay checkout for `?priceId=`.
 *
 * The customer is created (or looked up) server-side first so the overlay can be keyed by customer
 * id — the marketing site created the customer and then opened checkout by email anyway, which
 * loses the association.
 *
 * `checkout.completed` / `checkout.closed` are handled globally in PaddleInitializer.
 */
const CheckoutContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {showToast} = useToast();

  const user = useAppSelector(userSelector);
  const priceId = searchParams.get('priceId');

  const [error, setError] = useState<string | null>(null);
  // The overlay must only be opened once, even though effects re-run in StrictMode.
  const openedRef = useRef(false);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!priceId) {
      showToast('No plan selected', 'error');
      navigate('/account', {replace: true});
      return;
    }

    if (openedRef.current) {
      return;
    }
    openedRef.current = true;

    const run = async () => {
      try {
        const customerId = await dispatch(createPaddleCustomer({
          email: user.email || '',
          userId: user.id,
        })).unwrap();

        updatePaddleCustomer(customerId);

        const paddle = await initializePaddle();
        if (!paddle) {
          throw new Error('Checkout is unavailable right now. Please try again later.');
        }

        debugLog('[Checkout] Opening Paddle overlay for', priceId);
        openPaddleCheckout(paddle, {priceId, customerId, userId: user.id});
      } catch (err: any) {
        const message = err?.message || String(err);
        console.error('[Checkout] Failed to start checkout:', message);
        setError(message);
        showToast(`Could not start checkout: ${message}`, 'error');
        openedRef.current = false;
      }
    };

    run();
  }, [dispatch, navigate, priceId, showToast, user]);

  if (error) {
    return (
      <div className="max-w-md mx-auto mt-16 rounded-lg border border-red-600/30 bg-red-900/20 p-6 text-center">
        <h2 className="text-lg font-semibold text-white mb-2">Checkout could not start</h2>
        <p className="text-sm text-red-400 mb-6">{error}</p>
        <button
          onClick={() => navigate('/account')}
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-500 transition-colors font-medium"
        >
                    Back to account
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-16 text-center">
      <div className="flex items-center justify-center gap-3 text-purple-400">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
        <span className="text-lg">Opening checkout...</span>
      </div>
      <p className="mt-4 text-sm text-gray-400">
                Payment is handled by Paddle, our merchant of record.
      </p>
    </div>
  );
};

export default CheckoutContainer;
